import { Controller, useForm } from 'react-hook-form';
import { VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import { nonEmptyStringSchema } from 'common/zod/schemas';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import { CompanySupposedDirector } from '../../types';
import locales from './locales';

interface SelectYourselfProps {
  onNonOfficerClick: () => void;
  directors: CompanySupposedDirector[];
  onContinue: (director: CompanySupposedDirector) => void;
  onNotFound: () => void;
  isSending: boolean;
}

const selectYourselfSchema = z.object({
  directorId: nonEmptyStringSchema,
});

const SelectYourself = asBentoPage<SelectYourselfProps>(
  ({
    directors,
    isSending,
    Layout,
    onContinue,
    onNonOfficerClick,
    onNotFound,
    onPrev,
  }) => {
    const options: {
      label: string;
      value: string;
    }[] = directors.map(({ firstName, lastName, supposedBeneficiaryId }) => ({
      label: `${firstName.toLowerCase()} ${lastName.toUpperCase()}`,
      value: supposedBeneficiaryId,
    }));

    options.push({
      label: locales.anotherCorporateOfficer,
      value: 'NONE',
    });

    const {
      control,
      formState: { isValid },
      handleSubmit,
    } = useForm<z.infer<typeof selectYourselfSchema>>({
      mode: 'onChange',
      resolver: zodResolver(selectYourselfSchema),
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header title={locales.title} />

        <VStack
          alignItems="flex-start"
          as="form"
          onSubmit={handleSubmit(({ directorId }) =>
            directorId === 'NONE'
              ? onNotFound()
              : onContinue(
                  directors.find(
                    ({ supposedBeneficiaryId }) =>
                      supposedBeneficiaryId === directorId,
                  ) as CompanySupposedDirector,
                ),
          )}
          spacing="space-32"
        >
          <Controller
            control={control}
            name="directorId"
            render={({ field: { onChange, value } }) => (
              <Form.Radio.Group
                onChange={onChange}
                radioPosition="right"
                value={value ?? undefined}
                variant="outline"
                width="full"
              >
                {options.map((option) => (
                  <Form.Radio
                    backgroundColor="white"
                    key={option.value}
                    padding="space-16"
                    value={option.value}
                  >
                    <Form.Radio.Title
                      textTransform={
                        option.value !== 'NONE' ? 'capitalize' : undefined
                      }
                    >
                      {option.label}
                    </Form.Radio.Title>
                  </Form.Radio>
                ))}
              </Form.Radio.Group>
            )}
          />

          <Callout>
            <Callout.Content>{locales.notCorporateOfficer}</Callout.Content>

            <Callout.Button onClick={onNonOfficerClick}>
              {locales.clickHere}
            </Callout.Button>
          </Callout>

          <Button
            alignSelf="center"
            isDisabled={!isValid}
            isExpanded
            isLoading={isSending}
            marginTop="space-24"
            type="submit"
          >
            {locales.ctaContinue}
          </Button>
        </VStack>
      </Layout>
    );
  },
);

export default SelectYourself;
